import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OperatorPermission } from '../permissions';
import { api } from './client';

export interface Lot {
  id: string;
  code: string;
  shortCode: string;
  name: string;
  nameZh: string | null;
  description: string | null;
  accessNoteEn: string | null;
  accessNoteZh: string | null;
  addressLine: string;
  city: string;
  state: string;
  postalCode: string | null;
  latitude: number;
  longitude: number;
  timezone: string;
  capacity: number;
  /** Optional only for rolling deploys against an API version that predates this field. */
  spaceNumberingEnabled?: boolean;
  spaceNumberStart: number;
  heightLimitCm: number | null;
  evChargers: number;
  photos: string[];
  amenities: string[];
  status: 'draft' | 'online' | 'offline';
  offlineReason: string | null;
  availabilityStatus: 'unknown' | 'available' | 'busy' | 'full';
  availabilityUpdatedAt: string | null;
  businessHours: Record<string, Array<{ openMinute: number; closeMinute: number }>>;
  qr: { url: string; shortCode: string };
}

export interface RateTier {
  id: string;
  maxMinutes: number;
  baseCents: number;
}

export interface SpecialRate {
  id: string;
  name: string;
  kind: 'early_bird' | 'night' | 'weekend' | 'custom';
  baseCents: number;
  daysOfWeek: number[];
  entryFromMinute: number;
  entryToMinute: number;
  exitBeforeMinute: number | null;
  maxMinutes: number | null;
}

export interface RateVersion {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'superseded';
  minimumMinutes: number;
  incrementMinutes: number;
  dailyCapCents: number;
  extraDayCents: number;
  note: string | null;
  createdAt: string;
  publishedAt: string | null;
  effectiveFrom: string | null;
}

export interface RateDetail {
  version: RateVersion;
  tiers: RateTier[];
  specialRates: SpecialRate[];
  preview: Array<{ maxMinutes: number; baseCents: number; totalCents: number }>;
}

export interface Issue {
  level: 'error' | 'warning';
  code: string;
  message: string;
}

export interface Breakdown {
  requestedMinutes: number;
  entitlementMinutes: number;
  baseCents: number;
  totalCents: number;
  appliedRate: { source: 'tier' | 'special' | 'event'; id: string | null; name: string };
  comparedTierBaseCents: number | null;
}

export const keys = {
  lots: ['admin', 'lots'] as const,
  rates: (lotId: string) => ['admin', 'rates', lotId] as const,
  versions: (lotId: string) => ['admin', 'versions', lotId] as const,
  curve: (lotId: string, minutes: number) => ['admin', 'curve', lotId, minutes] as const,
  events: ['admin', 'events'] as const,
  live: (lotId?: string) => ['admin', 'live', lotId ?? 'all'] as const,
  orders: (params: string) => ['admin', 'orders', params] as const,
  revenue: (from: string, to: string) => ['admin', 'revenue', from, to] as const,
  payouts: ['admin', 'payouts'] as const,
  team: ['admin', 'team'] as const,
  audit: ['admin', 'audit'] as const,
  orderActivity: (sessionId: string) => ['admin', 'order-activity', sessionId] as const,
};

export const useLots = () =>
  useQuery({ queryKey: keys.lots, queryFn: () => api.get<{ lots: Lot[] }>('/admin/lots') });

export function useUpdateLot() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Lot>(`/admin/lots/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.lots });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useCreateLot() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Lot>('/admin/lots', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.lots });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useUploadLotPhoto() {
  return useMutation({
    mutationFn: (file: File) => api.upload<{ url: string }>('/admin/uploads/lot-photo', file),
  });
}

export const useRates = (lotId: string | undefined) =>
  useQuery({
    queryKey: keys.rates(lotId ?? ''),
    queryFn: () => api.get<RateDetail>(`/admin/lots/${lotId}/rates`),
    enabled: Boolean(lotId),
  });

export const useRateVersions = (lotId: string | undefined) =>
  useQuery({
    queryKey: keys.versions(lotId ?? ''),
    queryFn: () => api.get<{ versions: RateVersion[] }>(`/admin/lots/${lotId}/rate-versions`),
    enabled: Boolean(lotId),
  });

export function useSaveRates(lotId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      minimumMinutes: number;
      incrementMinutes: number;
      dailyCapCents: number;
      extraDayCents: number;
      tiers: Array<{ maxMinutes: number; baseCents: number }>;
      specialRates: Array<Omit<SpecialRate, 'id'>>;
      note?: string;
    }) => api.post<{ version: RateVersion; issues: Issue[] }>(`/admin/lots/${lotId}/rates`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.rates(lotId) });
      void client.invalidateQueries({ queryKey: keys.versions(lotId) });
    },
  });
}

export function usePublishRates(lotId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (rateVersionId: string) =>
      api.post<RateVersion>(`/admin/rate-versions/${rateVersionId}/publish`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.rates(lotId) });
      void client.invalidateQueries({ queryKey: keys.versions(lotId) });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export const useSimulate = (lotId: string) =>
  useMutation({
    mutationFn: (body: { startAt: string; durationMinutes: number }) =>
      api.post<{ breakdown: Breakdown; issues: Issue[] }>(`/admin/lots/${lotId}/simulate`, body),
  });

export const usePriceCurve = (lotId: string | undefined, durationMinutes: number) =>
  useQuery({
    queryKey: keys.curve(lotId ?? '', durationMinutes),
    queryFn: () =>
      api.get<{
        samples: Array<{ hour: number; totalCents: number; appliedRate: Breakdown['appliedRate'] }>;
      }>(`/admin/lots/${lotId}/price-curve?durationMinutes=${durationMinutes}`),
    enabled: Boolean(lotId),
  });

export interface ParkingEvent {
  id: string;
  name: string;
  venue: string | null;
  startsAt: string;
  endsAt: string;
  priceCents: number;
  lots: Array<{ id: string; name: string }>;
}

export const useEvents = () =>
  useQuery({ queryKey: keys.events, queryFn: () => api.get<{ events: ParkingEvent[] }>('/admin/events') });

export function useCreateEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      startsAt: string;
      endsAt: string;
      priceCents: number;
      lotIds: string[];
    }) => api.post<ParkingEvent>('/admin/events', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.events });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useDeleteEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/events/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.events }),
  });
}

export const useLiveSessions = (lotId?: string) =>
  useQuery({
    queryKey: keys.live(lotId),
    queryFn: () =>
      api.get<{
        count: number;
        sessions: Array<{
          id: string;
          plate: string;
          spaceNumber: number | null;
          lotName: string;
          startedAt: string;
          expiresAt: string;
          remainingSeconds: number;
          paidTotalCents: number;
        }>;
      }>(`/admin/sessions/live${lotId ? `?lotId=${lotId}` : ''}`),
    refetchInterval: 30_000,
  });

export const useOrders = (params: { lotId?: string; from?: string; to?: string; plate?: string }) => {
  const query = new URLSearchParams();
  if (params.lotId) query.set('lotId', params.lotId);
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.plate) query.set('plate', params.plate);
  const qs = query.toString();
  return useQuery({
    queryKey: keys.orders(qs),
    queryFn: () =>
      api.get<{
        orders: Array<{
          id: string;
          reference: string;
          plate: string;
          spaceNumber: number | null;
          lotName: string;
          startedAt: string;
          totalMinutes: number;
          paidTotalCents: number;
          refundedCents: number;
          extensionCount: number;
        }>;
      }>(`/admin/orders${qs ? `?${qs}` : ''}`),
  });
};

export interface OrderActivity {
  refunds: Array<{
    id: string;
    status: 'pending' | 'succeeded' | 'failed';
    amountCents: number;
    reason: string;
    createdAt: string;
  }>;
  notes: Array<{ id: string; operatorName: string; note: string; createdAt: string }>;
}

export const useOrderActivity = (sessionId: string | null) =>
  useQuery({
    queryKey: keys.orderActivity(sessionId ?? ''),
    queryFn: () => api.get<OrderActivity>(`/admin/orders/${sessionId}/activity`),
    enabled: Boolean(sessionId),
  });

export function useRefundOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason: string }) =>
      api.post(`/admin/orders/${sessionId}/refund`, { reason }),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: ['admin', 'orders'] });
      void client.invalidateQueries({ queryKey: keys.orderActivity(input.sessionId) });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useCorrectOrderPlate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; plate: string; plateState: string; reason: string }) =>
      api.patch(`/admin/orders/${input.sessionId}/plate`, {
        plate: input.plate,
        plateState: input.plateState,
        reason: input.reason,
      }),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: ['admin', 'orders'] });
      void client.invalidateQueries({ queryKey: keys.orderActivity(input.sessionId) });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useAddPatrolNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, note }: { sessionId: string; note: string }) =>
      api.post(`/admin/orders/${sessionId}/patrol-notes`, { note }),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: keys.orderActivity(input.sessionId) });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export const usePlateLookup = () =>
  useMutation({
    mutationFn: (plate: string) =>
      api.get<{
        plate: string;
        paid: boolean;
        session: { lotName: string; expiresAt: string; remainingSeconds: number } | null;
      }>(`/admin/plate-lookup?plate=${encodeURIComponent(plate)}`),
  });

export interface Revenue {
  transactionCount: number;
  baseTotalCents: number;
  platformFeeCents: number;
  grossCents: number;
  refundTotalCents: number;
  netCents: number;
  daily: Array<{ date: string; grossCents: number; count: number }>;
}

export const useRevenue = (from: string, to: string) =>
  useQuery({
    queryKey: keys.revenue(from, to),
    queryFn: () => api.get<Revenue>(`/admin/revenue?from=${from}&to=${to}`),
  });

export const usePayouts = () =>
  useQuery({
    queryKey: keys.payouts,
    queryFn: () =>
      api.get<{
        payouts: Array<{
          id: string;
          periodStart: string;
          periodEnd: string;
          netCents: number;
          status: string;
          paidAt: string | null;
          note: string | null;
        }>;
      }>('/admin/payouts'),
  });

export const useTeam = () =>
  useQuery({
    queryKey: keys.team,
    queryFn: () =>
      api.get<{
        lots: Array<{ id: string; code: string; name: string }>;
        members: Array<{
          id: string;
          name: string;
          email: string;
          role: 'owner' | 'staff';
          permissions: OperatorPermission[];
          lotIds: string[];
          disabled: boolean;
        }>;
        invitations: Array<{
          id: string;
          name: string;
          email: string;
          permissions: OperatorPermission[];
          lotIds: string[];
          expiresAt: string;
          createdAt: string;
        }>;
      }>('/admin/team'),
  });

export function useInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email: string;
      permissions: OperatorPermission[];
      lotIds: string[];
      locale: 'en' | 'zh';
    }) => api.post('/admin/team/invitations', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.team });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useUpdateMemberAccess() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      permissions,
      lotIds,
    }: {
      id: string;
      permissions: OperatorPermission[];
      lotIds: string[];
    }) => api.patch(`/admin/team/${id}`, { permissions, lotIds }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.team });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useRemoveMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/team/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.team });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useResendInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, locale }: { id: string; locale: 'en' | 'zh' }) =>
      api.post(`/admin/team/invitations/${id}/resend`, { locale }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.team });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export function useRevokeInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/team/invitations/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.team });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

export interface InvitationDetails {
  name: string;
  email: string;
  organizationName: string;
  permissions: OperatorPermission[];
  lots: Array<{ id: string; code: string; name: string }>;
  expiresAt: string;
}

export const useInvitationDetails = (token: string) =>
  useQuery({
    queryKey: ['admin', 'invitation', token],
    queryFn: () => api.post<InvitationDetails>('/admin/auth/invitations/preview', { token }),
    enabled: token.length >= 32,
    retry: false,
  });

export const useAudit = () =>
  useQuery({
    queryKey: keys.audit,
    queryFn: () =>
      api.get<{
        entries: Array<{ id: string; summary: string; actorName: string; createdAt: string }>;
      }>('/admin/audit-log?limit=30'),
  });

// ── Parking credit ───────────────────────────────────────────────────────────

export interface Promotion {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'ended';
  grantCents: number;
  totalBudgetCents: number;
  grantedCents: number;
  redeemedCents: number;
  grantCount: number;
  creditTtlDays: number | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

export interface PromotionGrant {
  id: string;
  deviceHash: string;
  userId: string;
  userEmail: string | null;
  amountCents: number;
  balanceCents: number;
  status: 'granted' | 'revoked';
  platform: string | null;
  appVersion: string | null;
  grantedAt: string;
  expiresAt: string | null;
}

export function usePromotions() {
  return useQuery({
    queryKey: ['promotions'],
    queryFn: () => api.get<{ promotions: Promotion[] }>('/admin/promotions'),
  });
}

export function useCreatePromotion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<Promotion>('/admin/promotions', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useUpdatePromotion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) =>
      api.patch<Promotion>(`/admin/promotions/${id}`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

/** `deviceId` is the raw identifier the app shows; the server hashes it to search. */
export function usePromotionGrants(params: { promotionId?: string; deviceId?: string }) {
  const query = new URLSearchParams();
  if (params.promotionId) query.set('promotionId', params.promotionId);
  if (params.deviceId) query.set('deviceId', params.deviceId);
  return useQuery({
    queryKey: ['promotion-grants', params.promotionId ?? '', params.deviceId ?? ''],
    queryFn: () => api.get<{ grants: PromotionGrant[] }>(`/admin/promotions/grants?${query.toString()}`),
  });
}

export function useRevokeGrant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => api.post<{ ok: true }>(`/admin/promotions/grants/${grantId}/revoke`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['promotion-grants'] });
      void client.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}
